import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Activity,
  Archive,
  BarChart3,
  BriefcaseBusiness,
  CalendarDays,
  ChartGantt,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  ContactRound,
  Database,
  Download,
  FileClock,
  FolderKanban,
  Handshake,
  Home,
  Kanban,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  ListTodo,
  LogOut,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Store,
  Timer,
  TrendingUp,
  UserCog,
  Users,
  Workflow,
  Zap,
} from "lucide-react";
import "./pm.css";
import { clearFileSession, listProjects, listPeople } from "./pmApi";
import { proxiedUrl } from "./blobUpload";
import { canViewPage } from "./accessControl";

const SETTINGS_GROUP_KEYS = ["project-settings", "schedule-import", "audit-log", "workflow", "automation", "permissions"];
const SIDEBAR_COLLAPSED_STORAGE_KEY = "bd-command-center:sidebar-collapsed";
const DEFAULT_OPEN_SECTIONS = {
  systemCalendar: false,
  demoWorkflow: false,
  salesImport: false,
  preProject: false,
  project: false,
  projectWork: false,
  liveReport: false,
  resource: false,
  secretary: false,
  user: false,
};

function sectionForNav(activeNav) {
  const sectionByNav = {
    calendar: "systemCalendar",
    "live-report-request-calendar": "systemCalendar",
    "demo-dashboard": "demoWorkflow",
    "demo-workflow": "demoWorkflow",
    "demo-installation-report": "demoWorkflow",
    "sales-import": "salesImport",
    "sales-import-customers": "salesImport",
    "sales-import-analytics": "salesImport",
    "sales-import-products": "salesImport",
    "sales-import-audit": "salesImport",
    "pre-project-dashboard": "preProject",
    "pre-project-list": "preProject",
    "pre-project-cancelled": "preProject",
    home: "projectWork",
    archive: "projectWork",
    templates: "projectWork",
    "resource-workload": "resource",
    teams: "resource",
    "resource-settings": "resource",
    "secretary-room": "secretary",
    people: "secretary",
    suppliers: "secretary",
    "me-dashboard": "user",
    "me-calendar": "user",
    "me-tasks": "user",
    "me-settings": "user",
  };
  if (activeNav?.startsWith("live-report-")) return sectionByNav[activeNav] ?? "liveReport";
  if (SETTINGS_GROUP_KEYS.includes(activeNav)) return "project";
  return sectionByNav[activeNav] ?? (activeNav ? "project" : null);
}

function openSectionsForActiveNav(activeNav) {
  const activeSection = sectionForNav(activeNav);
  return {
    ...DEFAULT_OPEN_SECTIONS,
    ...(activeSection ? { [activeSection]: true } : {}),
  };
}

function readStoredSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function NavIcon({ icon: Icon }) {
  return (
    <span className="pm-nav-icon" aria-hidden="true">
      <Icon size={18} strokeWidth={1.9} />
    </span>
  );
}

export default function PmShell({ project, activeNav, breadcrumb, viewRank = 3, children }) {
  const [allProjects, setAllProjects] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(() => SETTINGS_GROUP_KEYS.includes(activeNav));
  const [openSections, setOpenSections] = useState(() => openSectionsForActiveNav(activeNav));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readStoredSidebarCollapsed);
  const [people, setPeople] = useState([]);
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  useEffect(() => {
    setSettingsOpen(SETTINGS_GROUP_KEYS.includes(activeNav));
  }, [activeNav]);

  useEffect(() => {
    setOpenSections(openSectionsForActiveNav(activeNav));
  }, [activeNav]);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed)); } catch { /* ignore */ }
    if (sidebarCollapsed) setSwitcherOpen(false);
  }, [sidebarCollapsed]);

  async function handleLogout() {
    await clearFileSession();
    instance.logoutRedirect();
  }

  useEffect(() => {
    if (!account) return;
    listProjects().then(setAllProjects).catch(() => {});
  }, [account]);

  useEffect(() => {
    if (!account || !project?.id) { setPeople([]); return; }
    listPeople(project.id).then(setPeople).catch(() => setPeople([]));
  }, [account, project?.id]);

  const owner = project?.ownerEmail ? people.find(p => p.email === project.ownerEmail) : null;

  function toggleSection(key) {
    setOpenSections(prev => {
      const nextOpen = !prev[key];
      return {
        ...DEFAULT_OPEN_SECTIONS,
        [key]: nextOpen,
      };
    });
  }

  function NavSection({ id, title, children }) {
    const open = openSections[id] ?? true;
    return (
      <>
        <button type="button" className="pm-section-title pm-section-toggle" onClick={() => toggleSection(id)} title={sidebarCollapsed ? title : undefined}>
          <span className="pm-section-label-text">{title}</span>
          <span className="pm-section-caret" aria-hidden="true">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        </button>
        {(open || sidebarCollapsed) && <ul className="pm-nav-list">{children}</ul>}
      </>
    );
  }

  function NavLinkItem({ to, itemKey, icon, label }) {
    const active = activeNav === itemKey;
    return (
      <li>
        <Link to={to} className={`pm-nav-item ${active ? "active" : ""}`} title={sidebarCollapsed ? label : undefined}>
          <NavIcon icon={icon} />
          <span className="pm-nav-label">{label}</span>
        </Link>
      </li>
    );
  }

  const navItems = (project
    ? [
        { key: "overview", label: "ภาพรวม", href: `/projects/${project.id}/overview`, icon: LayoutDashboard },
        { key: "backlog", label: "Backlog", href: `/projects/${project.id}/backlog`, icon: ListTodo },
        { key: "board", label: "Board", href: `/projects/${project.id}/board`, icon: Kanban },
        { key: "gantt", label: "Gantt/Timeline", href: `/projects/${project.id}/gantt`, icon: ChartGantt },
        { key: "sprints", label: "Sprint", href: `/projects/${project.id}/sprints`, icon: Timer },
        { key: "workload", label: "Workload", href: `/projects/${project.id}/workload`, icon: Activity },
        { key: "budget", label: "งบประมาณ", href: `/projects/${project.id}/budget`, icon: CircleDollarSign },
      ]
    : []
  ).filter(item => canViewPage(item.key, viewRank));

  const settingsChildren = (project
    ? [
        { key: "project-settings", label: "ตั้งค่าทั่วไป", href: `/projects/${project.id}/project-settings`, icon: Settings },
        { key: "schedule-import", label: "ข้อมูลนำเข้า", href: `/projects/${project.id}/schedule-import`, icon: Download },
        { key: "workflow", label: "จัดการ Workflow", href: `/projects/${project.id}/workflow`, icon: Workflow },
        { key: "automation", label: "Automation", href: `/projects/${project.id}/automation`, icon: Zap },
        { key: "permissions", label: "สิทธิ์การเข้าถึง", href: `/projects/${project.id}/permissions`, icon: ShieldCheck },
        { key: "audit-log", label: "Audit Log", href: `/projects/${project.id}/audit-log`, icon: FileClock },
      ]
    : []
  ).filter(item => canViewPage(item.key, viewRank));

  return (
    <div className="pm-root">
      <div className={`pm-shell ${sidebarCollapsed ? "pm-shell-sidebar-collapsed" : ""}`}>
        <aside className="pm-sidebar">
          <div className="pm-sidebar-brand">
            <Link to="/projects" className="pm-brand-link" title="BD Command Center">
              <img src="/DTC_logo.png" alt="DTC" style={{ height: 26 }} />
              <span className="pm-brand-text">BD Command Center</span>
            </Link>
            <button
              type="button"
              className="pm-sidebar-collapse"
              onClick={() => setSidebarCollapsed(v => !v)}
              title={sidebarCollapsed ? "ขยายเมนู" : "ยุบเมนู"}
              aria-label={sidebarCollapsed ? "ขยายเมนู" : "ยุบเมนู"}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>

          {project && !sidebarCollapsed ? (
            <div style={{ position: "relative" }}>
              <div className="pm-switcher" onClick={() => setSwitcherOpen(o => !o)}>
                <div
                  className="pm-switcher-icon"
                  style={project.avatarUrl ? { background: `url(${proxiedUrl(project.avatarUrl)}) center/cover` } : undefined}
                >
                  {!project.avatarUrl && project.key.slice(0, 2)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="pm-switcher-name">{project.name}</div>
                  <div className="pm-switcher-key">{project.key}</div>
                </div>
              </div>
              {switcherOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 12, right: 12,
                  background: "#fff", border: "1px solid var(--pm-border)", borderRadius: 10,
                  boxShadow: "var(--pm-shadow-md)", zIndex: 50, padding: 6, maxHeight: 280, overflowY: "auto",
                }}>
                  {allProjects.map(p => (
                    <div
                      key={p.id}
                      onClick={() => { setSwitcherOpen(false); navigate(`/projects/${p.id}/board`); }}
                      style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--pm-surface-2)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <b>{p.name}</b> <span style={{ color: "var(--pm-ink-500)" }}>({p.key})</span>
                    </div>
                  ))}
                  <div
                    onClick={() => { setSwitcherOpen(false); navigate("/projects"); }}
                    style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 13, color: "var(--pm-brand)", fontWeight: 600 }}
                  >
                    ดูบริหารโครงการ
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <NavSection id="preProject" title="งานเตรียมเปิดโครงการ">
            <NavLinkItem to="/pre-project-control/dashboard" itemKey="pre-project-dashboard" icon={LayoutDashboard} label="Dashboard" />
            <NavLinkItem to="/pre-project-control/list" itemKey="pre-project-list" icon={ClipboardList} label="รายการงาน" />
            <NavLinkItem to="/pre-project-control/cancelled" itemKey="pre-project-cancelled" icon={Archive} label="งานยกเลิก" />
          </NavSection>

          <NavSection id="projectWork" title="งานโครงการ">
            <NavLinkItem to="/projects" itemKey="home" icon={Home} label="บริหารโครงการ" />
            <NavLinkItem to="/projects/archive" itemKey="archive" icon={Archive} label="คลังโปรเจกต์" />
            <NavLinkItem to="/templates" itemKey="templates" icon={LayoutTemplate} label="Templates" />
          </NavSection>

          {project && (
            <>
              <NavSection id="project" title="โปรเจกต์นี้">
                {navItems.map(item => (
                  <NavLinkItem key={item.key} to={item.href} itemKey={item.key} icon={item.icon} label={item.label} />
                ))}
                {settingsChildren.length > 0 && (
                  <li>
                    <div className="pm-nav-item" onClick={() => setSettingsOpen(o => !o)} style={{ cursor: "pointer" }} title={sidebarCollapsed ? "ตั้งค่าโปรเจกต์" : undefined}>
                      <NavIcon icon={Settings} />
                      <span className="pm-nav-label">ตั้งค่าโปรเจกต์</span>
                      <span className="pm-nav-subcaret" aria-hidden="true">
                        {settingsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                    </div>
                    {settingsOpen && (
                      <ul className="pm-nav-list" style={{ paddingLeft: 16 }}>
                        {settingsChildren.map(item => (
                          <NavLinkItem key={item.key} to={item.href} itemKey={item.key} icon={item.icon} label={item.label} />
                        ))}
                      </ul>
                    )}
                  </li>
                )}
              </NavSection>
            </>
          )}

          <NavSection id="systemCalendar" title="ปฏิทินระบบ">
            <NavLinkItem to="/calendar" itemKey="calendar" icon={CalendarDays} label="ปฏิทินโครงการ" />
            <NavLinkItem to="/live-report/request-calendar" itemKey="live-report-request-calendar" icon={ClipboardList} label="ปฏิทินงานรับแจ้ง" />
          </NavSection>

          <NavSection id="demoWorkflow" title="Demo workflow">
            <NavLinkItem to="/demo-dashboard" itemKey="demo-dashboard" icon={LayoutDashboard} label="Dashboard" />
            <NavLinkItem to="/demo-workflow" itemKey="demo-workflow" icon={FolderKanban} label="Demo workflow" />
            <NavLinkItem to="/demo-installation-report" itemKey="demo-installation-report" icon={BarChart3} label="รายงานการติดตั้งทดลอง" />
          </NavSection>

          <NavSection id="salesImport" title="ข้อมูลการขาย">
            <NavLinkItem to="/sales-import" itemKey="sales-import" icon={Database} label="ข้อมูลการขาย" />
            <NavLinkItem to="/sales-import/customers" itemKey="sales-import-customers" icon={ContactRound} label="Customer Detail" />
            <NavLinkItem to="/sales-import/analytics" itemKey="sales-import-analytics" icon={BarChart3} label="Sales Analytics" />
            <NavLinkItem to="/sales-import/products" itemKey="sales-import-products" icon={BriefcaseBusiness} label="Product Performance" />
            <NavLinkItem to="/sales-import/audit" itemKey="sales-import-audit" icon={FileClock} label="Audit Log" />
          </NavSection>

          <NavSection id="liveReport" title="ส่วนงานรับแจ้งจาก LineOA">
            <NavLinkItem to="/live-report/statistics" itemKey="live-report-dashboard" icon={LayoutDashboard} label="สถิติและ Activity" />
            <NavLinkItem to="/live-report/on-track" itemKey="live-report-on-track" icon={CheckCircle2} label="On Track" />
            <NavLinkItem to="/live-report/push" itemKey="live-report-push" icon={TrendingUp} label="Growth Project" />
            <NavLinkItem to="/live-report/strategic" itemKey="live-report-strategic" icon={ShieldCheck} label="Strategic Project" />
            <NavLinkItem to="/live-report/active-tasks" itemKey="live-report-active-tasks" icon={ListChecks} label="Active Tasks" />
            <NavLinkItem to="/live-report/cold-tasks" itemKey="live-report-cold-tasks" icon={FileClock} label="Cold Tasks" />
            <NavLinkItem to="/live-report/settings" itemKey="live-report-settings" icon={Settings} label="Settings" />
          </NavSection>

          <NavSection id="resource" title="RESOURCE MANAGEMENT">
            <NavLinkItem to="/resource-workload" itemKey="resource-workload" icon={BarChart3} label="Workload ทีม/ฝ่าย" />
            <NavLinkItem to="/teams" itemKey="teams" icon={Users} label="จัดการทีม" />
            <NavLinkItem to="/resource-settings" itemKey="resource-settings" icon={UserCog} label="ตั้งค่าบุคลากร" />
          </NavSection>

          <NavSection id="secretary" title="งานเลขา">
            <NavLinkItem to="/secretary-room" itemKey="secretary-room" icon={MessageSquare} label="ห้องทำงานเลขา" />
            <NavLinkItem to="/people" itemKey="people" icon={Users} label="รายชื่อบุคคล" />
            <NavLinkItem to="/suppliers" itemKey="suppliers" icon={Store} label="Supplier Pool" />
          </NavSection>

          <NavSection id="user" title="ข้อมูลผู้ใช้">
            <NavLinkItem to="/me/dashboard" itemKey="me-dashboard" icon={LayoutDashboard} label="Dashboard ของฉัน" />
            <NavLinkItem to="/me/work-calendar" itemKey="me-calendar" icon={CalendarDays} label="ปฏิทินงานของฉัน" />
            <NavLinkItem to="/me/tasks" itemKey="me-tasks" icon={CheckCircle2} label="งานที่ได้รับมอบหมาย" />
            <NavLinkItem to="/me/settings" itemKey="me-settings" icon={Settings} label="ตั้งค่าผู้ใช้" />
          </NavSection>

          <div className="pm-sidebar-footer">
            <div className="pm-switcher-icon" style={{ width: 32, height: 32 }}>
              {(account?.name ?? account?.username ?? "?").slice(0, 2)}
            </div>
            <div className="pm-sidebar-user">
              <div className="pm-sidebar-user-name">{account?.name ?? "ไม่ทราบชื่อ"}</div>
              <div className="pm-sidebar-user-email">{account?.username}</div>
            </div>
            <button className="pm-sidebar-logout" onClick={handleLogout} title="ออกจากระบบ" aria-label="ออกจากระบบ">
              <LogOut size={17} />
            </button>
          </div>
        </aside>

        <div className="pm-main">
          <header className="pm-topbar">
            <div className="pm-breadcrumbs">
              {project ? (
                <>
                  <span>{project.key}</span>
                  {breadcrumb && <><span className="pm-sep">/</span><span className="pm-crumb-muted">{breadcrumb}</span></>}
                </>
              ) : (
                <span>{breadcrumb ?? "บริหารโครงการ"}</span>
              )}
            </div>
            {project && (
              <div className="pm-topbar-actions">
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--pm-ink-500)" }}>
                  <span>เจ้าของโปรเจกต์:</span>
                  {owner ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span className="pm-switcher-icon" style={{ width: 20, height: 20, fontSize: 10 }}>{owner.name.slice(0, 2)}</span>
                      <b style={{ color: "var(--pm-ink-900)" }}>{owner.name}</b>
                    </span>
                  ) : project.ownerEmail ? (
                    <b style={{ color: "var(--pm-ink-900)" }}>{project.ownerEmail}</b>
                  ) : (
                    <span style={{ color: "var(--pm-ink-300)" }}>ยังไม่ระบุ</span>
                  )}
                </div>
              </div>
            )}
          </header>
          <main className="pm-content">{children}</main>
        </div>
      </div>
    </div>
  );
}


